import { argbIntToHex, LINE_DEFAULT_COLOR, LINE_DEFAULT_WIDTH, type GeoLineKind, type GeoLineStyle } from '@wehunt/ui'

/**
 * The map pin and map line payloads a natlinkedin share link carries, read the
 * way the app reads them.
 *
 * The app is the specification here: these follow drive's
 * `map_pin_deep_link_model.dart` and `map_line_deep_link_model.dart` — the
 * same required fields, the same accepted spellings, the same defaults — so a
 * link that opens in the app opens here, and one the app would reject is
 * rejected here too. Every value on the wire is a string.
 */

export interface MapPinLink {
  /** `location` for a dropped position, which the app shares with an empty pin id. */
  kind: 'pin' | 'location'
  name: string
  lat: number
  lon: number
  /** The pin type id, the vocabulary the pin art is keyed on. */
  type: string
  /** CSS colour; absent leaves the pin type's own colour. */
  color?: string
  /** A custom pin's glyph; never set for another type. */
  customSymbol?: string
  groupId: string
}

export interface MapLinePoint {
  lat: number
  lon: number
}

export interface MapLineLink {
  name: string
  kind: GeoLineKind
  points: MapLinePoint[]
  color: string
  style: GeoLineStyle
  /** The app's width units (default 10). */
  width: number
  groupId: string
}

type Payload = Record<string, unknown>

/** A value that carries something; empty counts as absent. For optional fields. */
function text(data: Payload, key: string): string | undefined {
  const value = data[key]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/**
 * A field the app requires to be PRESENT but lets be empty — its decoders reject
 * only a missing key. A dropped position is shared with an empty pin id AND an
 * empty team, so treating empty as absent here would turn every shared location
 * into an invalid link.
 */
function present(data: Payload, key: string): string | undefined {
  const value = data[key]
  return typeof value === 'string' ? value : undefined
}

function coordinate(raw: string | undefined, limit: number): number | null {
  if (raw === undefined) return null
  const value = Number(raw)
  return Number.isFinite(value) && Math.abs(value) <= limit ? value : null
}

/**
 * The app's colour: an 8-digit ARGB hex, `#` optional. Alpha is dropped, as the
 * team map drops it.
 */
function argbColor(raw: string | undefined): string | undefined {
  const hex = raw?.trim().replace(/^#/, '')
  if (hex === undefined || !/^[0-9a-f]{8}$/i.test(hex)) return undefined
  return argbIntToHex(Number.parseInt(hex, 16))
}

export function parseMapPinPayload(data: Payload): MapPinLink | null {
  const pinId = present(data, 'pinId')
  const groupId = present(data, 'groupId')
  const name = present(data, 'name')
  const type = present(data, 'type')
  const lat = coordinate(text(data, 'latitude'), 90)
  const lon = coordinate(text(data, 'longitude'), 180)
  if (
    pinId === undefined ||
    groupId === undefined ||
    name === undefined ||
    type === undefined ||
    lat === null ||
    lon === null
  ) {
    return null
  }
  return {
    // A dropped position is shared as a pin with no id: it was never saved, so
    // there is no pin to point at — only a place.
    kind: pinId === '' ? 'location' : 'pin',
    name,
    lat,
    lon,
    type,
    color: argbColor(text(data, 'color')),
    customSymbol: type === 'custom' ? text(data, 'customSymbol') : undefined,
    groupId,
  }
}

const LINE_STYLES: Record<string, GeoLineStyle> = {
  solid: 'solid',
  dashed: 'dashed',
  dash: 'dashed',
  dotted: 'dotted',
  dot: 'dotted',
  dash_dot: 'dash_dot',
}

/** `"lat,lon;lat,lon;…"`, latitude first. One bad pair rejects the line, as in the app. */
function linePoints(raw: string | undefined): MapLinePoint[] | null {
  if (raw === undefined) return null
  const points: MapLinePoint[] = []
  for (const pair of raw.split(';')) {
    const [latRaw, lonRaw, ...extra] = pair.split(',')
    const lat = coordinate(latRaw, 90)
    const lon = coordinate(lonRaw, 180)
    if (lat === null || lon === null || extra.length > 0) return null
    points.push({ lat, lon })
  }
  return points.length >= 2 ? points : null
}

function lineKind(data: Payload): GeoLineKind {
  const type = text(data, 'lineType')
  if (type === 'line' || type === 'arrow' || type === 'trail') return type
  // A line from before the type field existed says it is an arrow only through
  // its caps. The app draws each cap where it is; this page can head a line only
  // at its end, so only an end cap becomes an arrow — a start cap drawn at the
  // end would point the wrong way.
  return text(data, 'endCap') === 'arrow' ? 'arrow' : 'line'
}

export function parseMapLinePayload(data: Payload): MapLineLink | null {
  const lineId = present(data, 'lineId')
  const groupId = present(data, 'groupId')
  const name = present(data, 'name')
  const points = linePoints(text(data, 'coordinates'))
  if (lineId === undefined || groupId === undefined || name === undefined || points === null) return null
  const width = Number(text(data, 'lineWidth'))
  return {
    name,
    kind: lineKind(data),
    points,
    color: argbColor(text(data, 'color')) ?? LINE_DEFAULT_COLOR,
    style: LINE_STYLES[text(data, 'lineStyle') ?? ''] ?? 'solid',
    width: Number.isFinite(width) && width > 0 ? width : LINE_DEFAULT_WIDTH,
    groupId,
  }
}
